import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import { DoughnutGraph, GroupedBarGraph } from './Graphs';


// CustomCard overall mini cards
export const CustomCard = ({ metric }) => {
  const IconComp = metric.icon;
  const val = metric.value || 0;
  const lastVal = metric.lastValue || 0;
  const total = metric.total;
  
  const hasComparison = metric.lastValue !== undefined && metric.total !== undefined;
  
  const diff = val - lastVal;
  let percent = "0";
  if (lastVal > 0) {
    percent = Math.abs(Math.round((diff / lastVal) * 100));
  } else if (val > 0) {
    percent = "100";
  }

  // Display value: for Overall Statistics, the big number is total (e.g. 20, 86, 0, 2)
  const displayValue = total !== undefined ? total : val;

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Link to={metric.link || "#"} style={{ textDecoration: "none", display: "block", height: "100%" }}>
        <Paper
          id={metric.id}
          elevation={0}
          sx={{
            p: '16px 18px',
            borderRadius: '14px',
            bgcolor: metric.cardBg || '#f8fafc',
            border: '1px solid rgba(226, 232, 240, 0.95)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              bgcolor: '#ffffff',
              boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.06)',
              transform: 'translateY(-2px)',
              borderColor: 'rgba(203, 213, 225, 0.95)',
            },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            textAlign: 'left',
          }}
        >
          {/* Top Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, textAlign: 'left' }}>
            {IconComp && (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: metric.bg || 'rgba(37, 99, 235, 0.08)',
                  flexShrink: 0,
                }}
              >
                <IconComp sx={{ color: metric.color || '#2563eb', fontSize: 24 }} />
              </Box>
            )}

            <Box sx={{ minWidth: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 750,
                  color: '#3b4756ff',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  lineHeight: 1.2,
                  textAlign: 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {metric.label}
              </Typography>

              {metric.userName && (
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#1e293b',
                    mt: 0.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {metric.userName}
                </Typography>
              )}

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 0.4, textAlign: 'left' }}>
                <Typography
                  sx={{
                    fontSize: '1.85rem',
                    fontWeight: 800,
                    color: '#0f172a',
                    lineHeight: 1,
                    textAlign: 'left',
                  }}
                >
                  {displayValue}
                </Typography>

                {hasComparison && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '2px',
                      px: '8px',
                      py: '2px',
                      borderRadius: '20px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      bgcolor: diff > 0 ? '#dcfce7' : diff < 0 ? '#fee2e2' : '#f1f5f9',
                      color: diff > 0 ? '#15803d' : diff < 0 ? '#b91c1c' : '#334155',
                    }}
                  >
                    {diff > 0 ? `↑ ${percent}%` : diff < 0 ? `↓ ${percent}%` : `• 0%`}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Bottom Row / Comparison */}
          {hasComparison ? (
            <Box sx={{ mt: 1.6, pt: 1, borderTop: '1px solid rgba(241, 245, 249, 1)', textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  color: '#475569',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                This Month: <span style={{ fontWeight: 800, color: '#0f172a' }}>{val}</span>
                {"  |  "}
                Last Month: <span style={{ fontWeight: 800, color: '#0f172a' }}>{lastVal}</span>
              </Typography>
            </Box>
          ) : metric.valueLabel ? (
            <Box sx={{ mt: 1, textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  color: '#475569',
                  fontWeight: 600,
                  textAlign: 'left',
                }}
              >
                {metric.valueLabel}
              </Typography>
            </Box>
          ) : null}
        </Paper>
      </Link>
    </Grid>
  );
};

// SectionHeader component 
export const SectionHeader = ({ title, action, mt = 5 }) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt, mb: 2 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ height: 24, width: 4, bgcolor: "primary.main", borderRadius: "999px" }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        {title}
      </Typography>
    </Box>
    {action && <Box>{action}</Box>}
  </Box>
);

// graphical cards for all 3 graphs
export const SummarySection = ({ 
  controls, 
  chart, 
  stats, 
  summaryTitle, 
  footerNote, 
  footerIconColor = "warning.main",
  chartColSize = 5,
  statsColSize = 7,
  paperSx = { p: 3, borderRadius: 2, boxShadow: 1, borderColor: "divider" }
}) => {
  return (
    <>
      <Box>
        <Paper variant="outlined" sx={paperSx}>
          {controls && <Box sx={{ mb: 3, display: "flex", justifyContent: "left" }}>{controls}</Box>}
          
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={chartColSize}>
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
                {chart}
              </Box>
            </Grid>
            <Grid item xs={12} md={statsColSize}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "text.secondary", mb: 1 }}>
                  {summaryTitle}
                </Typography>

                <Grid container spacing={2}>
                  {stats.map((stat, i) => (
                    <Grid item xs={6} key={i}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, borderRadius: 2, bgcolor: stat.bg }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {stat.label}
                          </Typography>
                          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: stat.color, lineHeight: 1.2, mt: 0.3 }}>
                            {stat.value}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {footerNote && (
                  <Box sx={{ mt: 1, p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
                    <Typography component="div" variant="body2" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 4, height: 16, bgcolor: footerIconColor, borderRadius: 1 }} />
                      {footerNote}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

// project statistic 
export const ProjectStatistics = ({ 
  title = "Projects Statistics", 
  stats = {}, 
  timeframe, 
  onTimeframeChange,
}) => {
  const checkpoints = [
    { label: "Inward", value: stats.inward || 0 },
    { label: "Total Instruments", value: stats.totalInstruments || 0 },
    { label: "Calibrated", value: stats.calibrated || 0 },
    { label: "Pending Calibration", value: stats.pendingCalibration || 0 },
    { label: "Approved Certificates", value: stats.approvedCertificates || 0 },
    { label: "Pending Approve Certificates", value: stats.pendingApproved || 0 },
    { label: "Rejected Certificates", value: stats.rejectedCertificates || 0 },
    { label: "Amendment Certificates", value: stats.amendmentCertificates || 0 },
  ];

  return (
    <>
      <SectionHeader
        title={title}
        action={
          <Select
            value={timeframe}
            onChange={(e) => onTimeframeChange(e.target.value)}
            size="small"
            sx={{ minWidth: 150, bgcolor: "background.paper", borderRadius: 2 }}
          > 
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="monthly">This Month</MenuItem>
            <MenuItem value="overall">Overall</MenuItem>
          </Select>
        }
      />
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 1, borderColor: "divider" }}>
        <Box sx={{ width: "100%", py: 1 }}>
          
          {/* Track Line & Nodes Row */}
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", width: "100%", height: 32, mb: 2 }}>
            
            {/* Static Blue Track Line connecting Circle 1 (6.25%) to Circle 8 (93.75%) */}
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "6.25%",
                right: "6.25%",
                height: "5px",
                bgcolor: "#0284c7",
                transform: "translateY(-50%)",
                borderRadius: "3px",
                zIndex: 0,
              }}
            />

            {/* 6 Circle Nodes */}
            <Grid container spacing={0} sx={{ width: "100%", zIndex: 1 }}>
              {checkpoints.map((step, idx) => (
                <Grid item key={idx} sx={{ width: "12.5%", flexBasis: "12.5%", maxWidth: "12.5%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: "#0284c7",
                      border: "4px solid #0284c7",
                      boxSizing: "border-box",
                      boxShadow: "0 0 0 4px #e0f2fe",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        bgcolor: "#ffffff",
                      }}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Dynamic Values & Labels Grid */}
          <Grid container spacing={0}>
            {checkpoints.map((step, idx) => (
              <Grid item key={idx} sx={{ width: "12.5%", flexBasis: "12.5%", maxWidth: "12.5%" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    px: 0.5,
                  }}
                >
                  {/* Dynamic Value */}
                  <Typography
                    sx={{
                      fontSize: "1.35rem",
                      fontWeight: 800,
                      color: "#0369a1",
                      lineHeight: 1.2,
                      mb: 0.5,
                    }}
                  >
                    {step.value}
                  </Typography>

                  {/* Label */}
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: "text.primary",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

        </Box>
      </Paper>
    </>
  );
};

// Generic widget container card with subtle border and elevation
export const DashboardWidgetCard = ({ children, sx = {}, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      p: '20px 22px',
      borderRadius: '16px',
      bgcolor: '#ffffff',
      border: '1px solid rgba(226, 232, 240, 0.85)',
      boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

// Row 1 - Left: Calibration Trend (Grouped Bar Chart)
export const CalibrationTrendWidget = ({
  data,
  timeframe = "6months",
  onTimeframeChange,
}) => {
  return (
    <DashboardWidgetCard>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
          Calibration Trend
        </Typography>
        <Select
          value={timeframe}
          onChange={(e) => onTimeframeChange && onTimeframeChange(e.target.value)}
          size="small"
          sx={{
            minWidth: 120,
            height: 30,
            fontSize: '0.78rem',
            bgcolor: '#f8fafc',
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            fontWeight: 600,
            '& .MuiSelect-select': { py: '3px', px: '10px' },
          }}
        >
          <MenuItem value="6months" sx={{ fontSize: '0.78rem' }}>Last 6 Months</MenuItem>
          <MenuItem value="3months" sx={{ fontSize: '0.78rem' }}>Last 3 Months</MenuItem>
          <MenuItem value="year" sx={{ fontSize: '0.78rem' }}>This Year</MenuItem>
        </Select>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.2, mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#2563eb' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Completed</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Pending</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>Rejected</Typography>
        </Box>
      </Box>

      {/* Grouped Bar Chart */}
      <Box sx={{ height: 185, width: '100%', mt: 'auto' }}>
        <GroupedBarGraph data={data} />
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 1 - Center: Certificate Status (Donut Chart with central total and legend)
export const CertificateStatusWidget = ({
  stats = { total: 348, approved: 86, pending: 257, rejected: 5, amendment: 0 },
  timeframe = "monthly",
  onTimeframeChange,
}) => {
  const total = Number(stats.total) || 0;
  const approved = Number(stats.approved) || 0;
  const pending = Number(stats.pending) || 0;
  const rejected = Number(stats.rejected) || 0;
  const amendment = Number(stats.amendment) || 0;

  const getPct = (val) => (total > 0 ? Math.round((val / total) * 100) : 0);

  const doughnutData = {
    labels: ['Approved', 'Pending', 'Rejected', 'Amendment'],
    datasets: [
      {
        data: total > 0 ? [approved, pending, rejected, amendment] : [1],
        backgroundColor: total > 0
          ? ['#22c55e', '#f59e0b', '#ef4444', '#a855f7']
          : ['#e2e8f0'],
        borderWidth: 0,
        hoverOffset: 3,
      },
    ],
  };

  const statusItems = [
    { label: 'Approved', count: approved, pct: getPct(approved), color: '#22c55e' },
    { label: 'Pending', count: pending, pct: getPct(pending), color: '#f59e0b' },
    { label: 'Rejected', count: rejected, pct: getPct(rejected), color: '#ef4444' },
    { label: 'Amendment', count: amendment, pct: getPct(amendment), color: '#a855f7' },
  ];

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
          Certificate Status
        </Typography>
        <Select
          value={timeframe}
          onChange={(e) => onTimeframeChange && onTimeframeChange(e.target.value)}
          size="small"
          sx={{
            minWidth: 110,
            height: 30,
            fontSize: '0.78rem',
            bgcolor: '#f8fafc',
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            fontWeight: 600,
            '& .MuiSelect-select': { py: '3px', px: '10px' },
          }}
        >
          <MenuItem value="today" sx={{ fontSize: '0.78rem' }}>Today</MenuItem>
          <MenuItem value="monthly" sx={{ fontSize: '0.78rem' }}>This Month</MenuItem>
          <MenuItem value="overall" sx={{ fontSize: '0.78rem' }}>Overall</MenuItem>
        </Select>
      </Box>

      {/* Donut Chart & Legend */}
      <Grid container spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
        {/* Left Donut */}
        <Grid item xs={12} sm={5} md={5}>
          <Box sx={{ position: 'relative', width: 140, height: 140, mx: 'auto' }}>
            <DoughnutGraph data={doughnutData} cutout="70%" />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography sx={{ fontSize: '1.55rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
                {total}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', mt: 0.3 }}>
                Total
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right Status Breakdown */}
        <Grid item xs={12} sm={7} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, pl: { md: 1 } }}>
            {statusItems.map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: item.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>
                    {item.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>
                  {item.count} <span style={{ fontWeight: 500, color: '#64748b', fontSize: '0.72rem' }}>({item.pct}%)</span>
                </Typography>
              </Box>
            ))}
          </Box>
        </Grid>
      </Grid>
    </DashboardWidgetCard>
  );
};

// Row 1 - Right: Discipline Wise Calibration (Horizontal bars)
export const DisciplineWiseWidget = ({
  disciplines = [],
  timeframe = "monthly",
  onTimeframeChange,
}) => {
  const defaultDisciplines = [
    { name: 'Thermal', count: 8, color: '#0ea5e9' },
    { name: 'Dimensional', count: 5, color: '#14b8a6' },
    { name: 'Pressure', count: 3, color: '#a855f7' },
    { name: 'Electrical', count: 2, color: '#f97316' },
    { name: 'Mass', count: 1, color: '#f43f5e' },
    { name: 'Others', count: 0, color: '#94a3b8' },
  ];

  const items = disciplines && disciplines.length > 0 ? disciplines : defaultDisciplines;
  const maxCount = Math.max(...items.map((i) => i.count || 0), 1);

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
          Discipline Wise Calibration
        </Typography>
        <Select
          value={timeframe}
          onChange={(e) => onTimeframeChange && onTimeframeChange(e.target.value)}
          size="small"
          sx={{
            minWidth: 110,
            height: 30,
            fontSize: '0.78rem',
            bgcolor: '#f8fafc',
            borderRadius: '8px',
            borderColor: '#e2e8f0',
            fontWeight: 600,
            '& .MuiSelect-select': { py: '3px', px: '10px' },
          }}
        >
          <MenuItem value="today" sx={{ fontSize: '0.78rem' }}>Today</MenuItem>
          <MenuItem value="monthly" sx={{ fontSize: '0.78rem' }}>This Month</MenuItem>
          <MenuItem value="overall" sx={{ fontSize: '0.78rem' }}>Overall</MenuItem>
        </Select>
      </Box>

      {/* Progress Bars List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, flexGrow: 1, justifyContent: 'space-around' }}>
        {items.map((item, idx) => {
          const widthPct = Math.max(Math.round(((item.count || 0) / maxCount) * 100), item.count > 0 ? 8 : 0);
          return (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: '4px 6px',
                borderRadius: '8px',
                transition: 'background-color 150ms ease',
                '&:hover': { bgcolor: '#f8fafc' },
              }}
            >
              <Tooltip title={item.name} arrow placement="top">
                <Typography
                  sx={{
                    width: { xs: 120, sm: 145, md: 155 },
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    color: '#334155',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'default',
                  }}
                >
                  {item.name}
                </Typography>
              </Tooltip>

              <Box sx={{ flex: 1, height: 8, borderRadius: '4px', bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: `${widthPct}%`,
                    height: '100%',
                    borderRadius: '4px',
                    bgcolor: item.color || '#0284c7',
                    transition: 'width 0.4s ease',
                  }}
                />
              </Box>

              <Typography
                sx={{
                  minWidth: 26,
                  textAlign: 'right',
                  fontSize: '0.82rem',
                  fontWeight: 750,
                  color: item.count > 0 ? '#0f172a' : '#94a3b8',
                  flexShrink: 0,
                }}
              >
                {item.count || 0}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 2 - Left: Work Report mini cards (Top/Lowest Calibrator & Approver)
export const WorkReportCard = ({ metric }) => {
  const IconComp = metric.icon || EmojiEventsIcon;
  return (
    <Grid item xs={12} sm={6} md={3}>
      <Paper
        elevation={0}
        sx={{
          p: '16px 18px',
          borderRadius: '16px',
          bgcolor: '#ffffff',
          border: '1px solid rgba(226, 232, 240, 0.85)',
          boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02)',
          transition: 'all 200ms ease',
          '&:hover': {
            boxShadow: '0 8px 20px -4px rgba(0, 0, 0, 0.06)',
            transform: 'translateY(-2px)',
          },
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.6 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: metric.bg || 'rgba(37, 99, 235, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComp sx={{ color: metric.color || '#2563eb', fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 650, color: '#475569', letterSpacing: '0.02em' }}>
              {metric.label}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.88rem',
                fontWeight: 750,
                color: '#0f172a',
                mt: 0.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {metric.userName || 'N/A'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ mt: 1.2 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>
            {metric.value !== undefined ? metric.value : 0}
          </Typography>
        </Box>
      </Paper>
    </Grid>
  );
};

// Row 2 - Right: Today's Activity (Vertical List)
export const TodayActivityWidget = ({
  counts = {
    inwards: 6,
    datasheets: 8,
    generated: 5,
    pendingApproval: 12,
    approved: 4,
  },
}) => {
  const activities = [
    { label: 'Inwards Created', value: counts.inwards, icon: DownloadIcon, color: '#2563eb', bg: 'rgba(37, 99, 235, 0.08)', link: '/inward/count' },
    { label: 'Datasheets Completed', value: counts.datasheets, icon: ArticleIcon, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)', link: '/datasheet' },
    { label: 'Certificates Generated', value: counts.generated, icon: WorkspacePremiumIcon, color: '#4f46e5', bg: 'rgba(79, 70, 229, 0.08)', link: '/certificate' },
    { label: 'Pending for Approval', value: counts.pendingApproval, icon: WatchLaterIcon, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)', isRed: true, link: '/certificate?tab=approvalQueue' },
    { label: 'Certificates Approved', value: counts.approved, icon: CheckCircleIcon, color: '#16a34a', bg: 'rgba(22, 163, 74, 0.08)', link: '/certificate/today' },
  ];

  return (
    <DashboardWidgetCard>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.8 }}>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
          Today's Activity
        </Typography>
        <Link to="/certificate/today" style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 650, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
            View All →
          </Typography>
        </Link>
      </Box>

      {/* Activity List - Clean transparent rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8, flexGrow: 1, justifyContent: 'space-around' }}>
        {activities.map((act, idx) => {
          const IconComp = act.icon;
          return (
            <Link key={idx} to={act.link || "#"} style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: '7px',
                  px: '8px',
                  borderRadius: '8px',
                  transition: 'background-color 150ms ease',
                  '&:hover': {
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.3 }}>
                  <IconComp sx={{ color: act.color, fontSize: 19 }} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 550, color: '#334155' }}>
                    {act.label}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: '0.92rem',
                    fontWeight: 750,
                    color: act.isRed ? '#dc2626' : '#0f172a',
                    minWidth: 24,
                    textAlign: 'right',
                  }}
                >
                  {act.value || 0}
                </Typography>
              </Box>
            </Link>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

// Row 3 - Left: Recent Inwards Table (Latest 5)
export const RecentInwardsWidget = ({ inwards = [] }) => {
  const defaultInwards = [
    { id: 1, inwardNo: 'IN/2024/0125', client: 'Technico Industries', totalInstruments: 8, date: '16-Dec-2024', status: 'In Progress' },
    { id: 2, inwardNo: 'IN/2024/0124', client: 'ABC Calibration Pvt Ltd', totalInstruments: 5, date: '15-Dec-2024', status: 'Completed' },
    { id: 3, inwardNo: 'IN/2024/0123', client: 'Global Engineers', totalInstruments: 12, date: '14-Dec-2024', status: 'Pending' },
    { id: 4, inwardNo: 'IN/2024/0122', client: 'Precision Labs', totalInstruments: 3, date: '14-Dec-2024', status: 'Completed' },
    { id: 5, inwardNo: 'IN/2024/0121', client: 'Metro Instruments', totalInstruments: 7, date: '13-Dec-2024', status: 'In Progress' },
  ];

  const rows = inwards && inwards.length > 0 ? inwards : defaultInwards;

  const getStatusStyle = (status) => {
    const s = String(status || '').toLowerCase();
    if (s.includes('complete') || s === '1') {
      return { color: '#16a34a', dot: '#16a34a', label: 'Completed' };
    }
    if (s.includes('pending') || s === '0') {
      return { color: '#d97706', dot: '#d97706', label: 'Pending' };
    }
    return { color: '#2563eb', dot: '#2563eb', label: 'In Progress' };
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '14px 16px', sm: '16px 18px' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
          Recent Inwards
        </Typography>
        <Link to="/inward/count" style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
            View All
          </Typography>
        </Link>
      </Box>

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto', border: 'none' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '7px !important',
              px: '6px !important',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', pl: 0 }}>Inward No</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Client</TableCell>
              <TableCell align="center" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Inst.</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Date</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', pr: 0 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(0, 5).map((row, index) => {
              const status = getStatusStyle(row.status);
              return (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&:last-child td': { borderBottom: 'none !important' },
                  }}
                >
                  <TableCell sx={{ pl: 0, maxWidth: 110 }}>
                    <Tooltip title={row.inwardNo} arrow placement="top">
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 650, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.inwardNo}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 100 }}>
                    <Tooltip title={row.client} arrow placement="top">
                      <Typography sx={{ fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.client}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#334155' }}>
                    {row.totalInstruments}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {row.date}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: status.dot }} />
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: status.color, whiteSpace: 'nowrap' }}>
                        {status.label}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};

// Row 3 - Middle: Upcoming Due Dates Table (Latest 5)
export const UpcomingDueDatesWidget = ({ dueDates = [] }) => {
  const defaultDueDates = [
    { instrument: 'Pressure Gauge', ducId: 'PG-001', dueDate: '20-Dec-2024', daysLeft: 4 },
    { instrument: 'Thermometer', ducId: 'TM-024', dueDate: '22-Dec-2024', daysLeft: 6 },
    { instrument: 'Multimeter', ducId: 'MM-118', dueDate: '25-Dec-2024', daysLeft: 9 },
    { instrument: 'Torque Wrench', ducId: 'TW-009', dueDate: '28-Dec-2024', daysLeft: 12 },
    { instrument: 'Weighing Balance', ducId: 'WB-077', dueDate: '30-Dec-2024', daysLeft: 14 },
  ];

  const rows = dueDates && dueDates.length > 0 ? dueDates : defaultDueDates;

  const getUrgencyColor = (days) => {
    const d = Number(days);
    if (d <= 5) return '#dc2626';
    if (d <= 10) return '#ea580c';
    return '#16a34a';
  };

  return (
    <DashboardWidgetCard sx={{ p: { xs: '14px 16px', sm: '16px 18px' } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
          Upcoming Due Dates
        </Typography>
        <Link to="/scheduler" style={{ textDecoration: 'none' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2563eb', '&:hover': { textDecoration: 'underline' } }}>
            View All
          </Typography>
        </Link>
      </Box>

      {/* Table */}
      <TableContainer sx={{ overflowX: 'auto', border: 'none' }}>
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& th, & td': {
              border: 'none !important',
              borderBottom: '1px solid #f1f5f9 !important',
              py: '7px !important',
              px: '6px !important',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', pl: 0 }}>Instrument</TableCell>
              <TableCell sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Due Date</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.02em', pr: 0 }}>Due In</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(0, 5).map((row, index) => {
              const uColor = getUrgencyColor(row.daysLeft);
              return (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    '&:hover': { bgcolor: '#f8fafc' },
                    '&:last-child td': { borderBottom: 'none !important' },
                  }}
                >
                  <TableCell sx={{ pl: 0, maxWidth: 140 }}>
                    <Tooltip title={`${row.instrument} (${row.ducId})`} arrow placement="top">
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.instrument}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                    {row.dueDate}
                  </TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: uColor, whiteSpace: 'nowrap' }}>
                      {row.daysLeft}d left
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </DashboardWidgetCard>
  );
};

// Row 3 - Right: Quick Actions Panel
export const QuickActionsWidget = () => {
  const actions = [
    { label: 'Create Datasheet', icon: DescriptionOutlinedIcon, link: '/datasheet' },
    { label: 'Generate Certificate', icon: WorkspacePremiumOutlinedIcon, link: '/certificate' },
    { label: 'Approve Certificates', icon: VerifiedUserOutlinedIcon, link: '/certificate?tab=approvalQueue' },
    { label: 'Renew Inward', icon: AutorenewIcon, link: '/scheduler' },
  ];

  return (
    <DashboardWidgetCard sx={{ p: '20px' }}>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', mb: 1.6 }}>
        Quick Actions
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1, flexGrow: 1, justifyContent: 'center' }}>
        {/* Primary Action Button */}
        <Link to="/srf/create" style={{ textDecoration: 'none' }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              py: '7px',
              bgcolor: '#2563eb',
              color: '#ffffff',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 650,
              boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                bgcolor: '#1d4ed8',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
              },
            }}
          >
            Create Inward
          </Button>
        </Link>

        {/* Secondary Action Buttons */}
        {actions.map((act, idx) => {
          const IconComp = act.icon;
          return (
            <Link key={idx} to={act.link} style={{ textDecoration: 'none' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<IconComp sx={{ fontSize: 17 }} />}
                sx={{
                  py: '7px',
                  bgcolor: '#f8fafc',
                  color: '#2563eb',
                  borderColor: '#e2e8f0',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  justifyContent: 'flex-start',
                  px: 1.6,
                  '&:hover': {
                    bgcolor: '#eff6ff',
                    borderColor: '#bfdbfe',
                  },
                }}
              >
                {act.label}
              </Button>
            </Link>
          );
        })}
      </Box>
    </DashboardWidgetCard>
  );
};

